import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { doc, updateDoc } from 'firebase/firestore';
import { authService } from '@/services';
import { useCompanyStore } from '@/stores/company';
import { useAuthStore } from '@/stores/auth';
import { FirebaseProvider } from '@/infrastructure/providers/FirebaseProvider';
import type { User, LoginCredentials } from '@/types';

export const useEmailAuthStore = defineStore('emailAuth', () => {
  const user = ref<User | null>(null);
  const isAuthenticated = ref(false);

  // services/api.ts emite este evento cuando no hay refresh token o el backend
  // rechazó el refresh: la sesión de correo murió y hay que volver a entrar.
  // Antes cada petición seguía fallando con 401 en cadena sin que la UI lo
  // reflejara ("no envía correo").
  if (typeof window !== 'undefined') {
    window.addEventListener('email:session-expired', () => {
      user.value = null;
      isAuthenticated.value = false;
    });
  }
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const companyId = ref<string | null>(localStorage.getItem('emailCompanyId'));

  // Sesiones guardadas antes del arreglo de /auth/me traen el usuario envuelto en `user`.
  const userEmail = computed(() => user.value?.email || user.value?.user?.email || '');
  const userDisplayName = computed(
    () => user.value?.name || user.value?.displayName || user.value?.user?.name || ''
  );
  /**
   * Id de la conexión de correo (documento en emailConnections). Es la clave
   * con la que el backend guarda la carpeta de cada usuario en un correo
   * compartido (`folderByUser`). La sesión lo trae en distintas formas según
   * la versión con la que se guardó.
   */
  const mailboxUserId = computed(() => {
    const mailbox = user.value as any;
    const id = mailbox?.userId || mailbox?.id || mailbox?.user?.userId || mailbox?.user?.id;
    return id ? String(id) : '';
  });

  async function login(credentials?: LoginCredentials) {
    isLoading.value = true;
    error.value = null;

    try {
      const companyStore = useCompanyStore();
      const companyId = companyStore.selectedCompanyId || undefined;
      await authService.login(companyId);
      return true;
    } catch (err: any) {
      error.value = err.message || 'Error al iniciar sesión';
      isLoading.value = false;
      return false;
    }
  }

  async function loginMicrosoft() {
    isLoading.value = true;
    error.value = null;

    try {
      const companyStore = useCompanyStore();
      const companyId = companyStore.selectedCompanyId || undefined;
      await authService.login(companyId);
      return true;
    } catch (err: any) {
      error.value = err.message || 'Error al iniciar sesión con Microsoft';
      isLoading.value = false;
      return false;
    }
  }

  async function loginGoogle() {
    isLoading.value = true;
    error.value = null;

    try {
      const companyStore = useCompanyStore();
      const companyId = companyStore.selectedCompanyId || undefined;
      await authService.loginGoogle(companyId);
      return true;
    } catch (err: any) {
      error.value = err.message || 'Error al iniciar sesión con Google';
      isLoading.value = false;
      return false;
    }
  }

  /**
   * Deja en la conexión del buzón el uid de Firebase de quien la usa. El backend
   * de correo solo conoce el id del proveedor (OID/sub) y la dirección del buzón,
   * y esa dirección no siempre es la del acceso a Kila; las notificaciones
   * (KILA-362) necesitan el uid para llegar al usuario correcto. Es de cortesía:
   * si la escritura falla, la sesión sigue igual.
   */
  function linkMailboxToPlatformUser(targetCompanyId: string | null) {
    const providerUserId = mailboxUserId.value;
    const uid = (useAuthStore().user as { uid?: string } | null)?.uid;
    if (!targetCompanyId || !providerUserId || !uid) return;
    const db = FirebaseProvider.getInstance().getFirestore();
    updateDoc(doc(db, 'Companies', targetCompanyId, 'emailConnections', String(providerUserId)), {
      connectedByUid: uid,
    }).catch(() => {});
  }

  function setUser(userData: User) {
    user.value = userData;
  }

  function setAuthenticated(value: boolean) {
    isAuthenticated.value = value;
  }

  function setCompanyId(id: string) {
    companyId.value = id;
    localStorage.setItem('emailCompanyId', id);
    // La sesión activa pertenece a esta compañía: reflejarla en el mapa
    // `emailSessions` para poder recuperarla al volver. El callback de Google no
    // siempre trae companyId, y en ese caso saveTokens no la guardaba en el mapa.
    if (localStorage.getItem('accessToken')) {
      authService.saveCurrentSessionToMap(id);
    }
  }

  function saveTokens(data: Record<string, any>) {
    authService.saveTokens(data);
    if (data.companyId) {
      setCompanyId(data.companyId);
    }
  }

  async function logout() {
    isLoading.value = true;

    const currentCompanyId = companyId.value;

    try {
      await authService.logout();
      // Eliminar también la sesión del mapa emailSessions para esta compañía
      if (currentCompanyId) {
        authService.clearTokens(currentCompanyId);
      }
      // websocketService.disconnect(); // Deshabilitado: se usa Firebase Realtime

      user.value = null;
      isAuthenticated.value = false;
      companyId.value = null;
      error.value = null;
    } catch (err: any) {
      console.error('Error al cerrar sesión:', err);
    } finally {
      isLoading.value = false;
    }
  }

  async function checkAuth() {
    // La sesión del buzón es por compañía (mapa `emailSessions`), pero las claves
    // planas de localStorage son una sola: las de la última compañía conectada.
    // Si la compañía activa de la plataforma es otra hay que resolver primero la
    // sesión de esa compañía; si no, el buzón aparecía conectado con la cuenta de
    // la compañía anterior al cambiar de compañía fuera del buzón o al recargar.
    const companyStore = useCompanyStore();
    const activeCompanyId = companyStore.selectedCompanyId;
    if (activeCompanyId && localStorage.getItem('emailCompanyId') !== activeCompanyId) {
      await switchCompany(activeCompanyId);
      return;
    }

    const token = localStorage.getItem('accessToken');
    const userStr = localStorage.getItem('user');
    const expiresAt = localStorage.getItem('expiresAt');
    
    
    // Sin tokens → no autenticado (mostrar login)
    if (!token || !userStr) {
      return;
    }

    isLoading.value = true;

    // Restaurar usuario desde localStorage inmediatamente
    try {
      user.value = JSON.parse(userStr);
    } catch {
      clearEmailTokens();
      isLoading.value = false;
      return;
    }

    // Verificar si el token expiró
    const isTokenExpired = expiresAt ? Date.now() >= parseInt(expiresAt) : false;

    if (isTokenExpired) {
      const result = await authService.refreshSession();
      if (!result.ok) {
        if (result.permanent) {
          // Refresh token inválido/expirado: la sesión murió de verdad
          clearEmailTokens();
          user.value = null;
          isAuthenticated.value = false;
          isLoading.value = false;
          return;
        }
        // Fallo transitorio (red/5xx): conservar tokens y sesión;
        // el interceptor reintentará el refresh en la siguiente petición
      }
    }

    // Token válido (o recién refrescado) → marcar como autenticado
    isAuthenticated.value = true;

    // Intentar actualizar datos del usuario desde API (no bloquear si falla)
    try {
      const currentUser = await authService.getCurrentUser();
      if (currentUser) {
        user.value = currentUser;
        localStorage.setItem('user', JSON.stringify(currentUser));
      }
    } catch (err) {
    }

    linkMailboxToPlatformUser(activeCompanyId || companyId.value);
    isLoading.value = false;

    // websocketService: Deshabilitado, se usa Firebase Realtime en su lugar
  }

  function clearEmailTokens() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('expiresAt');
    localStorage.removeItem('emailProvider');
    localStorage.removeItem('emailCompanyId');
    localStorage.removeItem('user');
    companyId.value = null;
  }

  async function refreshToken() {
    const result = await authService.refreshSession();
    if (!result.ok && result.permanent) {
      await logout();
    }
    return result.ok;
  }

  async function switchCompany(newCompanyId: string): Promise<{ isAuthenticated: boolean }> {
    isLoading.value = true;

    // Guardar sesión actual en el mapa antes de cambiar. Las claves planas son de
    // la compañía en `emailCompanyId`, no del estado reactivo: así también se
    // conserva la sesión cuando se cambia de compañía tras recargar la página.
    const activeSessionCompanyId = localStorage.getItem('emailCompanyId');
    if (activeSessionCompanyId && localStorage.getItem('accessToken')) {
      authService.saveCurrentSessionToMap(activeSessionCompanyId);
    }

    // Limpiar estado reactivo
    user.value = null;
    isAuthenticated.value = false;

    // Intentar cargar sesión guardada para la nueva compañía
    const loaded = authService.loadSessionForCompany(newCompanyId);

    if (!loaded) {
      // Sin sesión para esta compañía: borrar las claves planas de la anterior.
      // Si se dejaban, seguían identificando al usuario y viajando como Bearer en
      // cada petición del buzón, y el siguiente checkAuth() las daba por buenas.
      authService.clearTokens();
      setCompanyId(newCompanyId);
      isLoading.value = false;
      return { isAuthenticated: false };
    }

    // Verificar si el token cargado sigue vigente
    try {
      const accessToken = localStorage.getItem('accessToken');
      const userStr = localStorage.getItem('user');
      const expiresAt = localStorage.getItem('expiresAt');

      if (!accessToken || !userStr) {
        companyId.value = newCompanyId;
        isLoading.value = false;
        return { isAuthenticated: false };
      }

      const isExpired = expiresAt ? Date.now() >= parseInt(expiresAt) : false;

      if (isExpired) {
        const result = await authService.refreshSession();
        if (!result.ok && result.permanent) {
          companyId.value = newCompanyId;
          isLoading.value = false;
          return { isAuthenticated: false };
        }
        // Fallo transitorio: continuar con la sesión cargada,
        // el interceptor reintentará el refresh en la siguiente petición
      }

      user.value = JSON.parse(userStr);
      isAuthenticated.value = true;
      companyId.value = newCompanyId;
      linkMailboxToPlatformUser(newCompanyId);

      // websocketService.connect: Deshabilitado, se usa Firebase Realtime en su lugar

      isLoading.value = false;
      return { isAuthenticated: true };
    } catch (err) {
      console.error('❌ switchCompany error:', err);
      companyId.value = newCompanyId;
      isLoading.value = false;
      return { isAuthenticated: false };
    }
  }

  function clearError() {
    error.value = null;
  }

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    companyId,
    userEmail,
    userDisplayName,
    mailboxUserId,
    login,
    loginMicrosoft,
    loginGoogle,
    logout,
    checkAuth,
    switchCompany,
    refreshToken,
    clearError,
    setUser,
    setAuthenticated,
    setCompanyId,
    saveTokens,
  };
});
